import { memo, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { getLogger } from '../../utilities/UseLog';
import { BaseDialog, DialogActionButton } from '../dialog-common';

const log = getLogger('NgpThreeButtonDialog');

const DEFAULT_CONFIRM_TEXT = i18n._(t`Confirm`);
const DEFAULT_DISABLE_TEXT = i18n._(t`Disable`);
const DEFAULT_CANCEL_TEXT = i18n._(t`Cancel`);

/**
 * A reusable three-button dialog component with Confirm, Disable, and Cancel actions.
 *
 * Features:
 * - Customizable button text and visibility
 * - Warning mode for destructive actions
 * - Async action handling with loading state
 * - Configurable close behavior
 * - Backdrop click prevention option
 *
 * @component
 * @example
 * <NgpThreeButtonDialog
 *   title="Confirm Action"
 *   open={open}
 *   setOpen={setOpen}
 *   onConfirm={handleConfirm}
 *   onDisable={handleDisable}
 *   isWarning
 * >
 *   <div>Are you sure?</div>
 * </NgpThreeButtonDialog>
 */
const NgpThreeButtonDialog = (props) => {
    const {
        title,
        children,
        open,
        setOpen,
        onCloseFunc,
        onConfirm,
        callerWillCloseOnConfirm = false,
        confirmButtonDisabled = false,
        confirmButtonText = DEFAULT_CONFIRM_TEXT,
        onDisable,
        callerWillCloseOnDisable = false,
        disableButtonDisabled = false,
        disableButtonText = DEFAULT_DISABLE_TEXT,
        cancelButtonText = DEFAULT_CANCEL_TEXT,
        showConfirmButton = true,
        showDisableButton = true,
        isWarning = false,
        disableBackdropClick = false,
        closeInTitleBar = true,
        confirmButtonProps,
        disableButtonProps,
        maxWidth = 'md',
    } = props;

    const [isActioning, setIsActioning] = useState(false);
    const dialogRef = useRef(null);

    /**
     * Handles cancel button click by delegating to BaseDialog's handleOnClose
     */
    const handleCancelClick = useCallback((event, reason) => {
        if (dialogRef.current && dialogRef.current.handleOnClose) {
            dialogRef.current.handleOnClose(event, reason);
        }
    }, []);

    /**
     * Handles confirm button click with error handling
     */
    const handleConfirmClick = useCallback(async () => {
        if (!onConfirm || isActioning) {
            return;
        }

        setIsActioning(true);
        try {
            await onConfirm();
        } catch (error) {
            log.error('Error in onConfirm:', error);
        } finally {
            setIsActioning(false);

            if (!callerWillCloseOnConfirm) {
                setOpen(false);
            }
        }
    }, [onConfirm, isActioning, callerWillCloseOnConfirm, setOpen]);

    /**
     * Handles disable button click with error handling
     */
    const handleDisableClick = useCallback(async () => {
        if (!onDisable || isActioning) {
            return;
        }

        setIsActioning(true);
        try {
            await onDisable();
        } catch (error) {
            log.error('Error in onDisable:', error);
        } finally {
            setIsActioning(false);

            if (!callerWillCloseOnDisable) {
                setOpen(false);
            }
        }
    }, [onDisable, isActioning, callerWillCloseOnDisable, setOpen]);

    /**
     * Memoized confirm button
     */
    const confirmButton = useMemo(
        () => (
            <DialogActionButton
                isWarning={isWarning}
                disabled={isWarning ? isActioning : confirmButtonDisabled || isActioning}
                onClick={handleConfirmClick}
                text={confirmButtonText}
                buttonProps={confirmButtonProps}
            />
        ),
        [isWarning, isActioning, confirmButtonDisabled, handleConfirmClick, confirmButtonText, confirmButtonProps]
    );

    /**
     * Memoized disable button
     */
    const disableButton = useMemo(
        () => (
            <DialogActionButton
                isWarning={isWarning}
                disabled={isWarning ? isActioning : disableButtonDisabled || isActioning}
                onClick={handleDisableClick}
                text={disableButtonText}
                buttonProps={disableButtonProps}
            />
        ),
        [isWarning, isActioning, disableButtonDisabled, handleDisableClick, disableButtonText, disableButtonProps]
    );

    /**
     * Memoized dialog actions
     */
    const actions = useMemo(
        () => (
            <>
                <Button disabled={isActioning} onClick={handleCancelClick} tabIndex={-1}>
                    {cancelButtonText}
                </Button>
                {showConfirmButton && confirmButton}
                {showDisableButton && disableButton}
            </>
        ),
        [
            isActioning,
            handleCancelClick,
            cancelButtonText,
            showConfirmButton,
            confirmButton,
            showDisableButton,
            disableButton,
        ]
    );

    return (
        <BaseDialog
            ref={dialogRef}
            title={title}
            open={open}
            setOpen={setOpen}
            onCloseFunc={onCloseFunc}
            actions={actions}
            disableBackdropClick={disableBackdropClick}
            closeInTitleBar={closeInTitleBar}
            maxWidth={maxWidth}
            isActioning={isActioning}
            onActioningChange={setIsActioning}
            dialogId="three-button-dialog"
        >
            {children}
        </BaseDialog>
    );
};

NgpThreeButtonDialog.propTypes = {
    /** Dialog title */
    title: PropTypes.node.isRequired,
    /** Dialog content */
    children: PropTypes.node,
    /** Controls whether the dialog is open */
    open: PropTypes.bool.isRequired,
    /** Function to set the open state */
    setOpen: PropTypes.func.isRequired,
    /** Optional callback when dialog closes */
    onCloseFunc: PropTypes.func,
    /** Callback for confirm button click */
    onConfirm: PropTypes.func,
    /** If true, caller is responsible for closing after confirm */
    callerWillCloseOnConfirm: PropTypes.bool,
    /** Disables the confirm button */
    confirmButtonDisabled: PropTypes.bool,
    /** Custom text for confirm button */
    confirmButtonText: PropTypes.node,
    /** Callback for disable button click */
    onDisable: PropTypes.func,
    /** If true, caller is responsible for closing after disable */
    callerWillCloseOnDisable: PropTypes.bool,
    /** Disables the disable button */
    disableButtonDisabled: PropTypes.bool,
    /** Custom text for disable button */
    disableButtonText: PropTypes.node,
    /** Custom text for cancel button */
    cancelButtonText: PropTypes.node,
    /** Shows/hides the confirm button */
    showConfirmButton: PropTypes.bool,
    /** Shows/hides the disable button */
    showDisableButton: PropTypes.bool,
    /** If true, buttons use warning (error) styling */
    isWarning: PropTypes.bool,
    /** If true, prevents closing on backdrop click */
    disableBackdropClick: PropTypes.bool,
    /** Shows/hides close button in title bar */
    closeInTitleBar: PropTypes.bool,
    /** Additional props for confirm button */
    confirmButtonProps: PropTypes.object,
    /** Additional props for disable button */
    disableButtonProps: PropTypes.object,
    /** Maximum width of the dialog */
    maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
};

NgpThreeButtonDialog.defaultProps = {
    children: null,
    onCloseFunc: null,
    onConfirm: null,
    callerWillCloseOnConfirm: false,
    confirmButtonDisabled: false,
    confirmButtonText: DEFAULT_CONFIRM_TEXT,
    onDisable: null,
    callerWillCloseOnDisable: false,
    disableButtonDisabled: false,
    disableButtonText: DEFAULT_DISABLE_TEXT,
    cancelButtonText: DEFAULT_CANCEL_TEXT,
    showConfirmButton: true,
    showDisableButton: true,
    isWarning: false,
    disableBackdropClick: false,
    closeInTitleBar: true,
    confirmButtonProps: null,
    disableButtonProps: null,
    maxWidth: 'md',
};

NgpThreeButtonDialog.displayName = 'NgpThreeButtonDialog';
export default memo(NgpThreeButtonDialog);
